import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase.js';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  collection,
  onSnapshot
} from 'firebase/firestore';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../constants/messages.js';

/**
 * Custom hook for managing user's movie library
 * Handles adding, removing movies and real-time sync with Firebase
 *
 * @param {Object} currentUser - The authenticated user object
 * @param {Function} showSnackbar - Function to show notifications
 * @returns {Object} Library state and management functions
 */
const useMovieLibrary = (currentUser, showSnackbar) => {
  const [userLibrary, setUserLibrary] = useState([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState('');

  /**
   * Check if a movie is in the user's library
   * @param {string} movieId - The ID of the movie to check
   * @returns {boolean} True if movie is in library
   */
  const isInLibrary = useCallback((movieId) => {
    return userLibrary.some((movie) => movie.id === movieId);
  }, [userLibrary]);

  /**
   * Get a movie from the library by ID
   * @param {string} movieId - The ID of the movie
   * @returns {Object|null} The movie object or null if not found
   */
  const getMovieFromLibrary = useCallback((movieId) => {
    return userLibrary.find((movie) => movie.id === movieId) || null;
  }, [userLibrary]);

  /**
   * Add a movie to the user's library
   * @param {Object} movie - The movie object to add
   * @returns {Promise<boolean>} True if successful
   */
  const addMovieToLibrary = useCallback(async (movie) => {
    if (!currentUser) {
      showSnackbar?.(ERROR_MESSAGES.UNAUTHORIZED, 'warning');
      return false;
    }

    try {
      const movieRef = doc(db, 'users', currentUser.uid, 'library', movie.id);
      const movieDoc = await getDoc(movieRef);

      if (movieDoc.exists()) {
        showSnackbar?.(`${movie.title} is already in your library.`, 'warning');
        return false;
      }

      // Add movie with default "all" category
      const movieData = { ...movie, categories: ['all'] };
      await setDoc(movieRef, movieData);
      showSnackbar?.(SUCCESS_MESSAGES.ADDED_TO_LIBRARY, 'success');
      return true;
    } catch (error) {
      console.error('Error adding movie to library:', error);
      showSnackbar?.(ERROR_MESSAGES.ADD_TO_LIBRARY_FAILED, 'error');
      setLibraryError(ERROR_MESSAGES.ADD_TO_LIBRARY_FAILED);
      return false;
    }
  }, [currentUser, showSnackbar]);

  /**
   * Remove a movie from the user's library
   * @param {Object} movie - The movie object to remove
   * @returns {Promise<boolean>} True if successful
   */
  const removeMovieFromLibrary = useCallback(async (movie) => {
    if (!currentUser) {
      showSnackbar?.(ERROR_MESSAGES.UNAUTHORIZED, 'warning');
      return false;
    }

    try {
      const movieRef = doc(db, 'users', currentUser.uid, 'library', movie.id);
      await deleteDoc(movieRef);
      showSnackbar?.(SUCCESS_MESSAGES.REMOVED_FROM_LIBRARY, 'success');
      return true;
    } catch (error) {
      console.error('Error removing movie from library:', error);
      showSnackbar?.(ERROR_MESSAGES.REMOVE_FROM_LIBRARY_FAILED, 'error');
      setLibraryError(ERROR_MESSAGES.REMOVE_FROM_LIBRARY_FAILED);
      return false;
    }
  }, [currentUser, showSnackbar]);

  /**
   * Add a movie to a specific category
   * @param {string} movieId - The ID of the movie
   * @param {string} categoryId - The ID of the category
   * @returns {Promise<boolean>} True if successful
   */
  const addMovieToCategory = useCallback(async (movieId, categoryId) => {
    if (!currentUser) {
      showSnackbar?.(ERROR_MESSAGES.UNAUTHORIZED, 'warning');
      return false;
    }

    try {
      const movieRef = doc(db, 'users', currentUser.uid, 'library', movieId);
      await updateDoc(movieRef, {
        categories: arrayUnion(categoryId),
      });
      showSnackbar?.(SUCCESS_MESSAGES.ADDED_TO_CATEGORY, 'success');
      return true;
    } catch (error) {
      console.error('Error adding movie to category:', error);
      showSnackbar?.(ERROR_MESSAGES.ADD_TO_CATEGORY_FAILED, 'error');
      return false;
    }
  }, [currentUser, showSnackbar]);

  /**
   * Remove a movie from a specific category
   * @param {string} movieId - The ID of the movie
   * @param {string} categoryId - The ID of the category
   * @returns {Promise<boolean>} True if successful
   */
  const removeMovieFromCategory = useCallback(async (movieId, categoryId) => {
    if (!currentUser) {
      showSnackbar?.(ERROR_MESSAGES.UNAUTHORIZED, 'warning');
      return false;
    }

    try {
      const movieRef = doc(db, 'users', currentUser.uid, 'library', movieId);
      await updateDoc(movieRef, {
        categories: arrayRemove(categoryId),
      });
      showSnackbar?.(SUCCESS_MESSAGES.REMOVED_FROM_CATEGORY, 'success');
      return true;
    } catch (error) {
      console.error('Error removing movie from category:', error);
      showSnackbar?.(ERROR_MESSAGES.REMOVE_FROM_CATEGORY_FAILED, 'error');
      return false;
    }
  }, [currentUser, showSnackbar]);

  /**
   * Get movies filtered by category
   * @param {string} categoryId - The ID of the category to filter by
   * @returns {Array} Array of movies in the category
   */
  const getMoviesByCategory = useCallback((categoryId) => {
    if (categoryId === 'all') {
      return userLibrary;
    }
    return userLibrary.filter((movie) =>
      movie.categories?.includes(categoryId)
    );
  }, [userLibrary]);

  /**
   * Clear the library error
   */
  const clearLibraryError = useCallback(() => {
    setLibraryError('');
  }, []);

  // Real-time listener for user's library
  useEffect(() => {
    if (!currentUser) {
      setUserLibrary([]);
      setIsLoadingLibrary(false);
      return;
    }

    setIsLoadingLibrary(true);
    const libraryRef = collection(db, 'users', currentUser.uid, 'library');

    const unsubscribe = onSnapshot(
      libraryRef,
      (snapshot) => {
        const moviesData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setUserLibrary(moviesData);
        setIsLoadingLibrary(false);
        setLibraryError('');
      },
      (error) => {
        console.error('Error fetching library:', error);
        setLibraryError(ERROR_MESSAGES.LIBRARY_LOAD_FAILED);
        setIsLoadingLibrary(false);
        showSnackbar?.(ERROR_MESSAGES.LIBRARY_LOAD_FAILED, 'error');
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [currentUser, showSnackbar]);

  return {
    // State
    userLibrary,
    isLoadingLibrary,
    libraryError,

    // Query functions
    isInLibrary,
    getMovieFromLibrary,
    getMoviesByCategory,

    // Mutation functions
    addMovieToLibrary,
    removeMovieFromLibrary,
    addMovieToCategory,
    removeMovieFromCategory,

    // Utility
    clearLibraryError,
  };
};

export default useMovieLibrary;
